import React from 'react';

const STORAGE_KEY = 'academy:sidebar-collapsed';

interface SidebarCollapseValue {
  isCollapsed: boolean;
  toggle: () => void;
}

const SidebarCollapseContext = React.createContext<SidebarCollapseValue>({
  isCollapsed: false,
  toggle: () => undefined,
});

const readStored = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const SidebarCollapseProvider = ({ children }: React.PropsWithChildren) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  React.useEffect(() => {
    setIsCollapsed(readStored());
  }, []);

  const toggle = React.useCallback(() => {
    setIsCollapsed((collapsed) => {
      const next = !collapsed;

      try {
        window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch {}

      return next;
    });
  }, []);

  const value = React.useMemo(() => ({ isCollapsed, toggle }), [isCollapsed, toggle]);

  return (
    <SidebarCollapseContext.Provider value={value}>{children}</SidebarCollapseContext.Provider>
  );
};

export const useSidebarCollapse = () => React.useContext(SidebarCollapseContext);
